import { readonly, ref, Ref } from "vue";
import axios, {
  type AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  CreateAxiosDefaults,
  InternalAxiosRequestConfig
} from "axios";
import useAxiosAuthentication from "../useAxiosAuthentication";
import { DEFAULT_HEADERS } from "./data.ts";

type ILoading = Ref<boolean>;
type IError = Ref<string | null>;
type IResponseData<T> = Ref<T | null>;

const axiosInstances: Record<string, AxiosInstance> = {};

const useAxios = <T>(
  baseURL: string = ""
): {
  loading: Readonly<ILoading>;
  error: Readonly<IError>;
  responseData: Readonly<IResponseData<T>>;
  axiosInstance: AxiosInstance;
  get: <T>(url: string, config?: AxiosRequestConfig, axiosDefaultResponse?: boolean) => Promise<T>;
  post: <T>(url: string, data?: any, config?: AxiosRequestConfig, axiosDefaultResponse?: boolean) => Promise<T>;
  put: <T>(url: string, data?: any, config?: AxiosRequestConfig, axiosDefaultResponse?: boolean) => Promise<T>;
  patch: <T>(url: string, data?: any, config?: AxiosRequestConfig, axiosDefaultResponse?: boolean) => Promise<T>;
  remove: (url: string, config?: AxiosRequestConfig, axiosDefaultResponse?: boolean) => Promise<null>;
} => {
  const loading: ILoading = ref(false);
  const error: IError = ref(null);
  const responseData: IResponseData<any> = ref(null);

  let axiosInstance = axiosInstances[baseURL];
  if (!axiosInstance) {
    const defaultConfig: CreateAxiosDefaults = {
      ...axios.defaults,
      baseURL,
      timeout: 20000
    };
    axiosInstance = axios.create(defaultConfig);

    useAxiosAuthentication(axiosInstance);

    axiosInstance.interceptors.request.use(
      async (request: InternalAxiosRequestConfig & any) => {
        if (request.baseURL.includes(`${import.meta.env.VITE_API_BASE_URL}`)) {
          request.headers = {
            ...request.headers,
            ...DEFAULT_HEADERS
          };
        }
        loading.value = true;
        error.value = null;
        return request;
      },
      (err: AxiosError) => {
        loading.value = false;
        error.value = err.message ?? "An error occurred";
        return Promise.reject(err);
      }
    );

    axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        loading.value = false;
        return response;
      },
      (err: AxiosError) => {
        loading.value = false;
        error.value = err.message ?? "An error occurred";
        return Promise.reject(err);
      }
    );

    axiosInstances[baseURL] = axiosInstance;
  }

  const get = async <T>(url: string, config?: AxiosRequestConfig, axiosDefaultResponse = false): Promise<T> => {
    try {
      const response = await axiosInstance.get<T>(url, config);
      responseData.value = response.data;
      if (axiosDefaultResponse) {
        return response as unknown as T;
      }
      return response.data;
    } catch (err) {
      if (axiosDefaultResponse) {
        throw new Error(err);
      }
      error.value = err.message ?? "An error occurred";
      throw new Error(error.value);
    } finally {
      loading.value = false;
    }
  };

  const post = async <T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
    axiosDefaultResponse = false
  ): Promise<T> => {
    try {
      const response = await axiosInstance.post<T>(url, data, config);
      responseData.value = response.data;
      if (axiosDefaultResponse) {
        return response as unknown as T;
      }
      return response.data;
    } catch (err) {
      if (axiosDefaultResponse) {
        throw new Error(err);
      }
      error.value = err.message ?? "An error occurred";
      throw new Error(error.value);
    } finally {
      loading.value = false;
    }
  };

  const put = async <T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
    axiosDefaultResponse = false
  ): Promise<T> => {
    try {
      const response = await axiosInstance.put<T>(url, data, config);
      responseData.value = response.data;
      if (axiosDefaultResponse) {
        return response as unknown as T;
      }
      return response.data;
    } catch (err) {
      if (axiosDefaultResponse) {
        throw new Error(err);
      }
      error.value = err.message ?? "An error occurred";
      throw new Error(error.value);
    } finally {
      loading.value = false;
    }
  };

  const patch = async <T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
    axiosDefaultResponse = false
  ): Promise<T> => {
    try {
      const response = await axiosInstance.patch<T>(url, data, config);
      responseData.value = response.data;
      if (axiosDefaultResponse) {
        return response as unknown as T;
      }
      return response.data;
    } catch (err) {
      if (axiosDefaultResponse) {
        throw new Error(err);
      }
      error.value = err.message ?? "An error occurred";
      throw new Error(error.value);
    } finally {
      loading.value = false;
    }
  };

  const remove = async (url: string, config?: AxiosRequestConfig, axiosDefaultResponse = false): Promise<null> => {
    try {
      await axiosInstance.delete(url, config);
      responseData.value = null;
      return null;
    } catch (err) {
      if (axiosDefaultResponse) {
        throw new Error(err);
      }
      error.value = err.message ?? "An error occurred";
      throw new Error(error.value);
    } finally {
      loading.value = false;
    }
  };

  return {
    loading: readonly(loading),
    error: readonly(error),
    responseData: readonly(responseData),
    axiosInstance,
    get,
    post,
    put,
    patch,
    remove
  };
};

export default useAxios;
